/***
 *
 *   COUNT POPOVER
 *   A count with a list behind it ("9 custom fields", "2 people", an email
 *   "Preview"). The count is a dotted-underline button; its list shows on
 *   hover, keyboard focus or click and hides on mouse leave, blur, scroll or
 *   Esc. The popover is portalled to document.body with fixed positioning,
 *   so it is never clipped by the diagram's scroll area or the drawer, and
 *   flips above the count when there is no room below.
 *
 *   PROPS
 *   value: the count text
 *   popover: { title, rows: [{ label, value }] } or { title, text }
 *
 **********/

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./workflow.module.scss";

let sequence = 0;

export function CountPopover({ value, popover }) {
  const idRef = useRef(null);
  if (!idRef.current) idRef.current = `wf-pop-${++sequence}`;

  const buttonRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 8, top: 8 });

  // place it under the count, or above when it would run off the screen
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !popRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const w = popRef.current.offsetWidth;
    const h = popRef.current.offsetHeight;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 12));
    let top = r.bottom + 6;
    if (top + h > window.innerHeight - 8) top = Math.max(8, r.top - h - 6);
    setPosition({ left, top });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const hide = () => setOpen(false);

    // capture phase: Esc dismisses the popover first, and stops there so a
    // drawer underneath stays open until the next Esc
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      hide();
    };

    window.addEventListener("scroll", hide, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("scroll", hide, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (!popover) return value ?? null;

  const show = () => setOpen(true);
  const hide = () => setOpen(false);
  const rows = Array.isArray(popover.rows) ? popover.rows : [];

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        className={styles.pv}
        aria-describedby={open ? idRef.current : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.stopPropagation();
          show();
        }}
      >
        {value}
      </button>

      {open &&
        ReactDOM.createPortal(
          <div className={styles.layer}>
            <div
              ref={popRef}
              id={idRef.current}
              role="tooltip"
              className={styles.pop}
              style={{ left: position.left, top: position.top }}
            >
              {popover.title && <div className={styles.popTitle}>{popover.title}</div>}
              {popover.text != null ? (
                <pre>{popover.text}</pre>
              ) : (
                <table>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i}>
                        <td>{row.label}</td>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
